import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { CheckCircleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import DateInput from '@/components/DateInput'
import MultiSelectFilter from '@/components/MultiSelectFilter'
import WarningBanner from '@/components/WarningBanner'
import {
  CollapsibleSectionCard,
  persistSectionOpen,
  readSectionOpenFromStorage,
  SectionCard,
} from '@/components/SectionCard'
import { incompleteRolesWarningMessage } from '@/components/payroll/paymasterSettings'
import {
  finalizeTipRunRevision,
  getTipRun,
  patchTipRunOverrides,
  recalculateTipRun,
  refreshTipRun,
  rollupPayoutsByEmployee,
  formatPayoutsByEmployeeClipboard,
  type PayoutsByEmployeeRow,
  type TipRunDetail,
  type TipRunLine,
  type TipRunRecalculateResult,
} from '@/services/tips'
import { useTipCalculatorStore } from '@/store/tipCalculatorStore'
import { toast } from '@/store/toastStore'
import { ColumnResizeHandle, useColumnWidths } from '@/tableControls'
import { formatApiError } from '@/utils/apiError'

const POOLS_OPEN_STORAGE_KEY = 'bookkeeper:tip-calculator:section-open:pools'
const DETAILS_OPEN_STORAGE_KEY = 'bookkeeper:tip-calculator:section-open:details'
const DETAIL_TABLE_PAGE_KEY = 'payroll-tip-calculator-details'
const PAYOUT_TABLE_PAGE_KEY = 'payroll-tip-calculator-payouts'
const LOCATION_TABLE_PAGE_KEY = 'payroll-tip-calculator-location'
const POOLS_TABLE_PAGE_KEY = 'payroll-tip-calculator-pools'
const CORRECTIONS_TABLE_PAGE_KEY = 'payroll-tip-calculator-corrections'

const TIP_CALCULATOR_TABLE_CLASS =
  'table-forecast tip-calculator-table border-separate border-spacing-0 min-w-full'

const DETAIL_COLUMN_KEYS = [
  'employee',
  'store',
  'date',
  'role',
  'eligibility',
  'group',
  'hours',
  'eligible_hours',
  'payout',
] as const

const DETAIL_DEFAULT_WIDTHS: Record<(typeof DETAIL_COLUMN_KEYS)[number], number> = {
  employee: 180,
  store: 140,
  date: 120,
  role: 160,
  eligibility: 140,
  group: 120,
  hours: 90,
  eligible_hours: 130,
  payout: 110,
}

const PAYOUT_COLUMN_KEYS = ['employee', 'store', 'hours', 'payout'] as const

const PAYOUT_DEFAULT_WIDTHS: Record<(typeof PAYOUT_COLUMN_KEYS)[number], number> = {
  employee: 200,
  store: 160,
  hours: 90,
  payout: 120,
}

const LOCATION_COLUMN_KEYS = ['store', 'pool', 'payout', 'unallocated', 'delta'] as const

const LOCATION_DEFAULT_WIDTHS: Record<(typeof LOCATION_COLUMN_KEYS)[number], number> = {
  store: 140,
  pool: 110,
  payout: 110,
  unallocated: 110,
  delta: 110,
}

const POOLS_COLUMN_KEYS = ['store', 'date', 'pool'] as const

const POOLS_DEFAULT_WIDTHS: Record<(typeof POOLS_COLUMN_KEYS)[number], number> = {
  store: 140,
  date: 120,
  pool: 110,
}

const CORRECTIONS_COLUMN_KEYS = ['store', 'employee', 'actual', 'corrected', 'delta'] as const

const CORRECTIONS_DEFAULT_WIDTHS: Record<(typeof CORRECTIONS_COLUMN_KEYS)[number], number> = {
  store: 140,
  employee: 180,
  actual: 110,
  corrected: 110,
  delta: 110,
}

function fillColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    boxSizing: 'border-box',
  }
}

function formatMoney(value: string | number | null | undefined): string {
  const num = Number(value ?? 0)
  if (!Number.isFinite(num)) return String(value ?? '')
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function formatHours(value: string | number | null | undefined): string {
  const num = Number(value ?? 0)
  if (!Number.isFinite(num)) return String(value ?? '')
  return num.toFixed(2)
}

function formatEligibilityNumber(value: string | number | null | undefined): string {
  const num = Number(value ?? 0)
  if (!Number.isFinite(num)) return String(value ?? '')
  return num.toFixed(2)
}

function formatEligibilityPct(value: string | number | null | undefined): string {
  return `${formatEligibilityNumber(value)}%`
}

function parseEligibilityPct(raw: string): number | null {
  const cleaned = raw.trim().replace(/%/g, '')
  const parsed = Number(cleaned)
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return null
  return Math.round(parsed * 100) / 100
}

function isReconciledDelta(delta: string | number | null | undefined): boolean {
  return Math.abs(Number(delta ?? 0)) < 0.005
}

function deltaTextClass(delta: string | number | null | undefined): string {
  return isReconciledDelta(delta) ? 'text-emerald-700' : 'text-amber-700'
}

function formatPayoutForClipboard(value: string | number | null | undefined): string {
  return Number(value ?? 0).toFixed(2)
}

async function copyPayoutToClipboard(value: string | number | null | undefined) {
  try {
    await navigator.clipboard.writeText(formatPayoutForClipboard(value))
    toast.success('Copied')
  } catch {
    toast.error('Failed to copy')
  }
}

async function copyPayoutsTableToClipboard(rows: PayoutsByEmployeeRow[]) {
  try {
    await navigator.clipboard.writeText(formatPayoutsByEmployeeClipboard(rows))
    toast.success('Copied')
  } catch {
    toast.error('Failed to copy')
  }
}

function uniqueSortedNames(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])).sort(
    (a, b) => a.localeCompare(b)
  )
}

function matchesSelectedNames(name: string | null | undefined, selected: string[]): boolean {
  if (selected.length === 0) return true
  return selected.includes(name?.trim() || '')
}

function TableFilterBar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-end gap-3">{children}</div>
}

function SearchableMultiFilter({
  id,
  label,
  values,
  selected,
  onChange,
}: {
  id: string
  label: string
  values: string[]
  selected: string[]
  onChange: (selected: string[]) => void
}) {
  const options = useMemo(
    () => values.map((value) => ({ id: value, label: value })),
    [values]
  )
  if (values.length <= 1) return null
  return (
    <div className="w-52">
      <MultiSelectFilter
        id={id}
        label={label}
        options={options}
        selected={selected}
        onChange={onChange}
        placeholder="All"
        searchable
      />
    </div>
  )
}

function ResizableTh({
  columnKey,
  width,
  onStartResize,
  children,
  className = '',
}: {
  columnKey: string
  width: number
  onStartResize: (key: string, event: React.MouseEvent) => void
  children: ReactNode
  className?: string
}) {
  return (
    <th className={`relative ${className}`} style={fillColumnStyle(width)}>
      {children}
      <ColumnResizeHandle columnKey={columnKey} onStartResize={onStartResize} />
    </th>
  )
}

function CopyablePayoutCell({
  amount,
  style,
}: {
  amount: string | number | null | undefined
  style?: CSSProperties
}) {
  return (
    <td className="text-right" style={style}>
      <button
        type="button"
        className="group inline-flex items-center justify-end gap-1.5 hover:bg-gray-50"
        title="Copy payout"
        aria-label="Copy payout"
        onClick={() => void copyPayoutToClipboard(amount)}
      >
        {formatMoney(amount)}
        <ClipboardDocumentIcon
          className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-gray-600"
          aria-hidden
        />
      </button>
    </td>
  )
}

function PayoutsByEmployeeTable({
  rows,
  onEmployeeClick,
}: {
  rows: PayoutsByEmployeeRow[]
  onEmployeeClick?: (employeeName: string) => void
}) {
  const { getWidth, startResize, totalWidth } = useColumnWidths({
    pageKey: PAYOUT_TABLE_PAGE_KEY,
    columnKeys: [...PAYOUT_COLUMN_KEYS],
    defaults: PAYOUT_DEFAULT_WIDTHS,
  })
  const tableStyle = useMemo(
    (): CSSProperties => ({
      width: '100%',
      minWidth: totalWidth,
      tableLayout: 'fixed',
    }),
    [totalWidth]
  )
  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => fillColumnStyle(getWidth(columnKey)),
    [getWidth]
  )

  return (
    <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
      <table className={TIP_CALCULATOR_TABLE_CLASS} style={tableStyle}>
        <thead>
          <tr>
            <ResizableTh columnKey="employee" width={getWidth('employee')} onStartResize={startResize}>
              Employee
            </ResizableTh>
            <ResizableTh columnKey="store" width={getWidth('store')} onStartResize={startResize}>
              Store
            </ResizableTh>
            <ResizableTh
              columnKey="hours"
              width={getWidth('hours')}
              onStartResize={startResize}
              className="text-right"
            >
              Hours
            </ResizableTh>
            <ResizableTh
              columnKey="payout"
              width={getWidth('payout')}
              onStartResize={startResize}
              className="text-right"
            >
              Payout
            </ResizableTh>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.store}-${row.employee_id}`}>
              <td style={cellStyle('employee')}>
                {onEmployeeClick ? (
                  <button
                    type="button"
                    className="text-left hover:text-brand-700 hover:underline"
                    onClick={() => onEmployeeClick(row.employee_name)}
                  >
                    {row.employee_name}
                  </button>
                ) : (
                  row.employee_name
                )}
              </td>
              <td style={cellStyle('store')}>{row.store}</td>
              <td className="text-right" style={cellStyle('hours')}>
                {formatHours(row.hours)}
              </td>
              <CopyablePayoutCell amount={row.total} style={cellStyle('payout')} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DetailedTipCalculationsTable({
  lines,
  isDraft,
  savingLineId,
  onEligibilityBlur,
}: {
  lines: TipRunLine[]
  isDraft: boolean
  savingLineId: number | null
  onEligibilityBlur: (line: TipRunLine, nextValue: string) => void
}) {
  const { getWidth, startResize, totalWidth } = useColumnWidths({
    pageKey: DETAIL_TABLE_PAGE_KEY,
    columnKeys: [...DETAIL_COLUMN_KEYS],
    defaults: DETAIL_DEFAULT_WIDTHS,
  })
  const tableStyle = useMemo(
    (): CSSProperties => ({
      width: '100%',
      minWidth: totalWidth,
      tableLayout: 'fixed',
    }),
    [totalWidth]
  )
  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => fillColumnStyle(getWidth(columnKey)),
    [getWidth]
  )

  return (
    <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
      <table className={TIP_CALCULATOR_TABLE_CLASS} style={tableStyle}>
        <thead>
          <tr>
            <ResizableTh columnKey="employee" width={getWidth('employee')} onStartResize={startResize}>
              Employee
            </ResizableTh>
            <ResizableTh columnKey="store" width={getWidth('store')} onStartResize={startResize}>
              Store
            </ResizableTh>
            <ResizableTh columnKey="date" width={getWidth('date')} onStartResize={startResize}>
              Date
            </ResizableTh>
            <ResizableTh columnKey="role" width={getWidth('role')} onStartResize={startResize}>
              Role
            </ResizableTh>
            <ResizableTh
              columnKey="eligibility"
              width={getWidth('eligibility')}
              onStartResize={startResize}
              className="text-right"
            >
              Eligibility %
            </ResizableTh>
            <ResizableTh columnKey="group" width={getWidth('group')} onStartResize={startResize}>
              Tip group
            </ResizableTh>
            <ResizableTh
              columnKey="hours"
              width={getWidth('hours')}
              onStartResize={startResize}
              className="text-right"
            >
              Hours
            </ResizableTh>
            <ResizableTh
              columnKey="eligible_hours"
              width={getWidth('eligible_hours')}
              onStartResize={startResize}
              className="text-right"
            >
              Eligible hours
            </ResizableTh>
            <ResizableTh
              columnKey="payout"
              width={getWidth('payout')}
              onStartResize={startResize}
              className="text-right"
            >
              Payout
            </ResizableTh>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id}>
              <td style={cellStyle('employee')}>{line.employee_name}</td>
              <td style={cellStyle('store')}>{line.store}</td>
              <td style={cellStyle('date')}>{line.date}</td>
              <td style={cellStyle('role')}>{line.role_name || '—'}</td>
              <td className="text-right" style={cellStyle('eligibility')}>
                {isDraft ? (
                  <div className="flex items-center justify-end gap-1">
                    <input
                      key={line.id}
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      defaultValue={formatEligibilityNumber(line.eligibility_pct)}
                      className="w-full max-w-[5.5rem] rounded-md border border-gray-300 px-2 py-1 text-right text-sm leading-normal"
                      disabled={savingLineId === line.id}
                      onBlur={(event) => onEligibilityBlur(line, event.target.value)}
                    />
                    <span className="text-gray-500">%</span>
                  </div>
                ) : (
                  formatEligibilityPct(line.eligibility_pct)
                )}
              </td>
              <td style={cellStyle('group')}>{line.role_group}</td>
              <td className="text-right" style={cellStyle('hours')}>
                {formatHours(line.total_hours)}
              </td>
              <td className="text-right" style={cellStyle('eligible_hours')}>
                {formatHours(line.eligible_hours)}
              </td>
              <td className="text-right" style={cellStyle('payout')}>
                {formatMoney(line.payout_amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function PayoutsByLocationTable({
  detail,
  storeFilter,
}: {
  detail: TipRunDetail
  storeFilter: string[]
}) {
  const { getWidth, startResize, totalWidth } = useColumnWidths({
    pageKey: LOCATION_TABLE_PAGE_KEY,
    columnKeys: [...LOCATION_COLUMN_KEYS],
    defaults: LOCATION_DEFAULT_WIDTHS,
  })
  const tableStyle = useMemo(
    (): CSSProperties => ({
      width: '100%',
      minWidth: totalWidth,
      tableLayout: 'fixed',
    }),
    [totalWidth]
  )
  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => fillColumnStyle(getWidth(columnKey)),
    [getWidth]
  )

  const rows = useMemo(() => {
    const stores = new Set([
      ...Object.keys(detail.payouts_by_location),
      ...Object.keys(detail.reconciliation),
    ])
    return Array.from(stores)
      .sort()
      .map((store) => {
        const recon = detail.reconciliation[store]
        return {
          store,
          pool: recon?.pool ?? '0',
          payout: detail.payouts_by_location[store] ?? recon?.payouts ?? '0',
          unallocated: recon?.unallocated ?? '0',
          delta: recon?.delta ?? '0',
        }
      })
      .filter((row) => matchesSelectedNames(row.store, storeFilter))
  }, [detail, storeFilter])

  const footerMessages = useMemo(() => {
    const messages: string[] = []
    for (const row of rows) {
      const delta = Number(row.delta)
      const unallocated = Number(row.unallocated)
      if (!isReconciledDelta(delta)) {
        messages.push(`${row.store}: ${formatMoney(delta)} delta`)
      } else if (Math.abs(unallocated) >= 0.005) {
        messages.push(`${row.store}: ${formatMoney(unallocated)} unallocated`)
      }
    }
    for (const warning of detail.warnings) {
      messages.push(`${warning.store}: ${warning.reason}`)
    }
    return messages
  }, [detail.warnings, rows])

  const allReconciled = footerMessages.length === 0

  return (
    <>
      <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
        <table className={TIP_CALCULATOR_TABLE_CLASS} style={tableStyle}>
          <thead>
            <tr>
              <ResizableTh columnKey="store" width={getWidth('store')} onStartResize={startResize}>
                Store
              </ResizableTh>
              <ResizableTh
                columnKey="pool"
                width={getWidth('pool')}
                onStartResize={startResize}
                className="text-right"
              >
                Pool
              </ResizableTh>
              <ResizableTh
                columnKey="payout"
                width={getWidth('payout')}
                onStartResize={startResize}
                className="text-right"
              >
                Payout
              </ResizableTh>
              <ResizableTh
                columnKey="unallocated"
                width={getWidth('unallocated')}
                onStartResize={startResize}
                className="text-right"
              >
                Unallocated
              </ResizableTh>
              <ResizableTh
                columnKey="delta"
                width={getWidth('delta')}
                onStartResize={startResize}
                className="text-right"
              >
                Delta
              </ResizableTh>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.store}>
                <td style={cellStyle('store')}>{row.store}</td>
                <td className="text-right" style={cellStyle('pool')}>
                  {formatMoney(row.pool)}
                </td>
                <CopyablePayoutCell amount={row.payout} style={cellStyle('payout')} />
                <td className="text-right" style={cellStyle('unallocated')}>
                  {formatMoney(row.unallocated)}
                </td>
                <td className={`text-right ${deltaTextClass(row.delta)}`} style={cellStyle('delta')}>
                  {formatMoney(row.delta)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {allReconciled ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700">
          <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>Pools and payouts reconciled</span>
        </div>
      ) : (
        <p className="mt-3 text-sm text-gray-600">{footerMessages.join(' · ')}</p>
      )}
    </>
  )
}

function DailyTipPoolsTable({
  pools,
}: {
  pools: Array<{ store: string; date: string; amount: string }>
}) {
  const { getWidth, startResize, totalWidth } = useColumnWidths({
    pageKey: POOLS_TABLE_PAGE_KEY,
    columnKeys: [...POOLS_COLUMN_KEYS],
    defaults: POOLS_DEFAULT_WIDTHS,
  })
  const tableStyle = useMemo(
    (): CSSProperties => ({
      width: '100%',
      minWidth: totalWidth,
      tableLayout: 'fixed',
    }),
    [totalWidth]
  )
  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => fillColumnStyle(getWidth(columnKey)),
    [getWidth]
  )

  return (
    <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
      <table className="table-forecast border-separate border-spacing-0 min-w-full" style={tableStyle}>
        <thead>
          <tr>
            <ResizableTh columnKey="store" width={getWidth('store')} onStartResize={startResize}>
              Store
            </ResizableTh>
            <ResizableTh columnKey="date" width={getWidth('date')} onStartResize={startResize}>
              Date
            </ResizableTh>
            <ResizableTh
              columnKey="pool"
              width={getWidth('pool')}
              onStartResize={startResize}
              className="text-right"
            >
              Pool
            </ResizableTh>
          </tr>
        </thead>
        <tbody>
          {pools.map((pool) => (
            <tr key={`${pool.store}-${pool.date}`}>
              <td style={cellStyle('store')}>{pool.store}</td>
              <td style={cellStyle('date')}>{pool.date}</td>
              <td className="text-right" style={cellStyle('pool')}>
                {formatMoney(pool.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ActualVsCorrectedTable({
  corrections,
}: {
  corrections: TipRunRecalculateResult['diff']['corrections']
}) {
  const { getWidth, startResize, totalWidth } = useColumnWidths({
    pageKey: CORRECTIONS_TABLE_PAGE_KEY,
    columnKeys: [...CORRECTIONS_COLUMN_KEYS],
    defaults: CORRECTIONS_DEFAULT_WIDTHS,
  })
  const tableStyle = useMemo(
    (): CSSProperties => ({
      width: '100%',
      minWidth: totalWidth,
      tableLayout: 'fixed',
    }),
    [totalWidth]
  )
  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => fillColumnStyle(getWidth(columnKey)),
    [getWidth]
  )

  return (
    <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
      <table className="table-forecast border-separate border-spacing-0 min-w-full" style={tableStyle}>
        <thead>
          <tr>
            <ResizableTh columnKey="store" width={getWidth('store')} onStartResize={startResize}>
              Store
            </ResizableTh>
            <ResizableTh columnKey="employee" width={getWidth('employee')} onStartResize={startResize}>
              Employee
            </ResizableTh>
            <ResizableTh
              columnKey="actual"
              width={getWidth('actual')}
              onStartResize={startResize}
              className="text-right"
            >
              Actual
            </ResizableTh>
            <ResizableTh
              columnKey="corrected"
              width={getWidth('corrected')}
              onStartResize={startResize}
              className="text-right"
            >
              Corrected
            </ResizableTh>
            <ResizableTh
              columnKey="delta"
              width={getWidth('delta')}
              onStartResize={startResize}
              className="text-right"
            >
              Delta
            </ResizableTh>
          </tr>
        </thead>
        <tbody>
          {corrections.map((row) => (
            <tr key={`${row.store}-${row.employee_id}`}>
              <td style={cellStyle('store')}>{row.store}</td>
              <td style={cellStyle('employee')}>
                {row.first_name} {row.last_name}
              </td>
              <td className="text-right" style={cellStyle('actual')}>
                {formatMoney(row.actual)}
              </td>
              <td className="text-right" style={cellStyle('corrected')}>
                {formatMoney(row.corrected)}
              </td>
              <td className="text-right" style={cellStyle('delta')}>
                {formatMoney(row.delta)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function TipCalculatorTab() {
  const startDate = useTipCalculatorStore((s) => s.startDate)
  const endDate = useTipCalculatorStore((s) => s.endDate)
  const activeRunId = useTipCalculatorStore((s) => s.activeRunId)
  const setStartDate = useTipCalculatorStore((s) => s.setStartDate)
  const setEndDate = useTipCalculatorStore((s) => s.setEndDate)
  const setActiveRunId = useTipCalculatorStore((s) => s.setActiveRunId)
  const expireIfStale = useTipCalculatorStore((s) => s.expireIfStale)

  const [detail, setDetail] = useState<TipRunDetail | null>(null)
  const [recalcResult, setRecalcResult] = useState<TipRunRecalculateResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [calculating, setCalculating] = useState(false)
  const [refreshingCalcs, setRefreshingCalcs] = useState(false)
  const [savingLineId, setSavingLineId] = useState<number | null>(null)
  const [storeFilter, setStoreFilter] = useState<string[]>([])
  const [locationStoreFilter, setLocationStoreFilter] = useState<string[]>([])
  const [payoutStoreFilter, setPayoutStoreFilter] = useState<string[]>([])
  const [detailEmployeeFilter, setDetailEmployeeFilter] = useState<string[]>([])
  const [payoutEmployeeFilter, setPayoutEmployeeFilter] = useState<string[]>([])
  const [detailsOpen, setDetailsOpen] = useState(() => readSectionOpenFromStorage(DETAILS_OPEN_STORAGE_KEY))

  const handleDetailsOpenChange = useCallback((next: boolean) => {
    setDetailsOpen(next)
    persistSectionOpen(DETAILS_OPEN_STORAGE_KEY, next)
  }, [])

  useEffect(() => {
    expireIfStale()
  }, [expireIfStale])

  const loadRun = useCallback(async (runId: number) => {
    const next = await getTipRun(runId)
    setDetail(next)
    setActiveRunId(runId)
    return next
  }, [setActiveRunId])

  useEffect(() => {
    if (!activeRunId) return
    void loadRun(activeRunId).catch((err) => {
      console.error(err)
      toast.error(formatApiError(err, 'Failed to load tip run'))
    })
  }, [activeRunId, loadRun])

  const stores = useMemo(() => {
    if (!detail) return []
    return Array.from(new Set(detail.lines.map((line) => line.store).filter(Boolean) as string[])).sort()
  }, [detail])

  const locationStores = useMemo(() => {
    if (!detail) return []
    const storeSet = new Set([
      ...Object.keys(detail.payouts_by_location),
      ...Object.keys(detail.reconciliation),
    ])
    return Array.from(storeSet).sort()
  }, [detail])

  const detailEmployeeNames = useMemo(
    () => uniqueSortedNames(detail?.lines.map((line) => line.employee_name) ?? []),
    [detail]
  )

  const filteredLines = useMemo(() => {
    if (!detail) return []
    return detail.lines.filter((line) => {
      if (!matchesSelectedNames(line.store, storeFilter)) return false
      return matchesSelectedNames(line.employee_name, detailEmployeeFilter)
    })
  }, [detail, detailEmployeeFilter, storeFilter])

  const employeePayouts = useMemo(
    () => (detail ? rollupPayoutsByEmployee(detail.lines) : []),
    [detail]
  )

  const payoutEmployeeNames = useMemo(
    () => uniqueSortedNames(employeePayouts.map((row) => row.employee_name)),
    [employeePayouts]
  )

  const filteredEmployeePayouts = useMemo(() => {
    return employeePayouts.filter((row) => {
      if (!matchesSelectedNames(row.store, payoutStoreFilter)) return false
      return matchesSelectedNames(row.employee_name, payoutEmployeeFilter)
    })
  }, [employeePayouts, payoutEmployeeFilter, payoutStoreFilter])

  const pools = detail?.run.pools_snapshot ?? []
  const isDraft = detail?.run.status === 'draft'
  const isFinalized = detail?.run.status === 'finalized'

  async function handleRefresh() {
    if (!startDate || !endDate) {
      toast.error('Select a start and end date')
      return
    }
    setCalculating(true)
    setDetail(null)
    setRecalcResult(null)
    try {
      const next = await refreshTipRun(startDate, endDate)
      setDetail(next)
      setActiveRunId(next.run.id)
      if (next.sync_warnings?.length) {
        toast.warning(next.sync_warnings.join('; '))
      } else {
        toast.success('Tips calculated')
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to calculate tips'))
    } finally {
      setCalculating(false)
    }
  }

  async function handleRefreshCalcs() {
    if (!startDate || !endDate) {
      toast.error('Select a start and end date')
      return
    }
    setRefreshingCalcs(true)
    setRecalcResult(null)
    try {
      const next = await refreshTipRun(startDate, endDate, true)
      setDetail(next)
      setActiveRunId(next.run.id)
      toast.success('Calcs refreshed')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to refresh calcs'))
    } finally {
      setRefreshingCalcs(false)
    }
  }

  async function handleLineEligibilityBlur(line: TipRunLine, nextValue: string) {
    if (!detail || !isDraft) return
    const parsed = parseEligibilityPct(nextValue)
    if (parsed === null) {
      toast.error('Eligibility must be between 0 and 100')
      return
    }
    if (parsed === parseEligibilityPct(String(line.eligibility_pct))) return
    setSavingLineId(line.id)
    try {
      const next = await patchTipRunOverrides(detail.run.id, {
        line_ids: [line.id],
        eligibility_pct: parsed.toFixed(2),
      })
      setDetail(next)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save eligibility'))
    } finally {
      setSavingLineId(null)
    }
  }

  async function handleRecalculate() {
    if (!detail) return
    setLoading(true)
    try {
      const next = await recalculateTipRun(detail.run.id)
      setRecalcResult(next)
      setDetail(next.corrected)
      setActiveRunId(next.corrected.run.id)
      toast.success('Correction draft created')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to recalculate tip run'))
    } finally {
      setLoading(false)
    }
  }

  async function handleFinalizeRevision() {
    if (!detail || !recalcResult) return
    setLoading(true)
    try {
      const next = await finalizeTipRunRevision(
        detail.run.id,
        recalcResult.original.run.id
      )
      setDetail(next)
      setRecalcResult(null)
      toast.success('Revision finalized')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to finalize revision'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <label className="text-sm text-gray-700">
          Start
          <DateInput
            value={startDate}
            onChange={(value) => setStartDate(value)}
            dateFormat="iso"
            className="input mt-1 text-sm"
          />
        </label>
        <label className="text-sm text-gray-700">
          End
          <DateInput
            value={endDate}
            onChange={(value) => setEndDate(value)}
            dateFormat="iso"
            className="input mt-1 text-sm"
          />
        </label>
        <Button
          gradient
          onClick={() => void handleRefresh()}
          disabled={calculating || refreshingCalcs || loading || !startDate || !endDate}
        >
          {calculating ? 'Calculating tips…' : 'Calculate tips'}
        </Button>
        {detail ? (
          <>
            <Button
              variant="outline"
              onClick={() => void handleRefreshCalcs()}
              disabled={calculating || refreshingCalcs || loading || !startDate || !endDate}
            >
              {refreshingCalcs ? 'Refreshing…' : 'Refresh calcs'}
            </Button>
            {isFinalized ? (
              <Button
                variant="outline"
                onClick={() => void handleRecalculate()}
                disabled={loading || refreshingCalcs}
              >
                Recalculate
              </Button>
            ) : null}
            {recalcResult && isDraft ? (
              <Button onClick={() => void handleFinalizeRevision()} disabled={loading || refreshingCalcs}>
                Finalize revision
              </Button>
            ) : null}
          </>
        ) : null}
      </div>

      {detail?.incomplete_roles?.length ? (
        <WarningBanner message={incompleteRolesWarningMessage(detail.incomplete_roles)} />
      ) : null}

      {detail?.sync_warnings?.length ? (
        <WarningBanner message={detail.sync_warnings.join('; ')} />
      ) : null}

      {calculating ? (
        <SectionCard>
          <p className="text-sm text-gray-500">Calculating tips…</p>
        </SectionCard>
      ) : detail ? (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            <SectionCard
              title="Payouts by location"
              headerRight={
                <TableFilterBar>
                  <SearchableMultiFilter
                    id="tip-location-store-filter"
                    label="Store"
                    values={locationStores}
                    selected={locationStoreFilter}
                    onChange={setLocationStoreFilter}
                  />
                </TableFilterBar>
              }
            >
              <PayoutsByLocationTable detail={detail} storeFilter={locationStoreFilter} />
            </SectionCard>
            <SectionCard
              title="Payouts by employee"
              titleAccessory={
                <button
                  type="button"
                  className="rounded p-0.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                  title="Copy table"
                  aria-label="Copy payouts table including headers"
                  onClick={() => void copyPayoutsTableToClipboard(filteredEmployeePayouts)}
                >
                  <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
                </button>
              }
              headerRight={
                <TableFilterBar>
                  <SearchableMultiFilter
                    id="tip-payouts-employee-filter"
                    label="Employee"
                    values={payoutEmployeeNames}
                    selected={payoutEmployeeFilter}
                    onChange={setPayoutEmployeeFilter}
                  />
                  <SearchableMultiFilter
                    id="tip-payouts-store-filter"
                    label="Store"
                    values={stores}
                    selected={payoutStoreFilter}
                    onChange={setPayoutStoreFilter}
                  />
                </TableFilterBar>
              }
            >
              <PayoutsByEmployeeTable
                rows={filteredEmployeePayouts}
                onEmployeeClick={(employeeName) => {
                  setDetailEmployeeFilter([employeeName.trim()])
                  if (!detailsOpen) {
                    handleDetailsOpenChange(true)
                  }
                }}
              />
            </SectionCard>
          </div>

          <CollapsibleSectionCard
            storageKey={DETAILS_OPEN_STORAGE_KEY}
            open={detailsOpen}
            onOpenChange={handleDetailsOpenChange}
            title="Detailed tip calculations"
            headerRight={
              <TableFilterBar>
                <SearchableMultiFilter
                  id="tip-details-employee-filter"
                  label="Employee"
                  values={detailEmployeeNames}
                  selected={detailEmployeeFilter}
                  onChange={setDetailEmployeeFilter}
                />
                <SearchableMultiFilter
                  id="tip-details-store-filter"
                  label="Store"
                  values={stores}
                  selected={storeFilter}
                  onChange={setStoreFilter}
                />
              </TableFilterBar>
            }
          >
            <DetailedTipCalculationsTable
              lines={filteredLines}
              isDraft={isDraft}
              savingLineId={savingLineId}
              onEligibilityBlur={(line, nextValue) => void handleLineEligibilityBlur(line, nextValue)}
            />
          </CollapsibleSectionCard>

          <CollapsibleSectionCard storageKey={POOLS_OPEN_STORAGE_KEY} title="Daily tip pools">
            <DailyTipPoolsTable pools={pools} />
          </CollapsibleSectionCard>

          {recalcResult ? (
            <SectionCard title="Actual vs corrected">
              <ActualVsCorrectedTable corrections={recalcResult.diff.corrections} />
            </SectionCard>
          ) : null}

          {detail.warnings.length > 0 ? (
            <WarningBanner
              message={detail.warnings
                .map((warning) => `${warning.store} ${warning.date}: ${warning.reason}`)
                .join('; ')}
            />
          ) : null}
        </>
      ) : (
        <SectionCard>
          <p className="text-sm text-gray-500">
            Choose a date range and calculate tips to load tip pools and allocations.
          </p>
        </SectionCard>
      )}
    </div>
  )
}
